import { groupBy, keyBy } from "lodash";
import pluralize from "pluralize";
import React, { ReactNode } from "react";
import styled from "styled-components";

import { gql, UnwrapGraphQLResult } from "@examspark/api";
import { sortObjectivesItems } from "@examspark/common";
import {
  fromAWSDate,
  shortLocale,
} from "@examspark/common/dist/utils/coursePlanner";
import { Toolbar, colorFromTheme } from "@examspark/common-ui/server";
import {
  Course,
  TeksCategory,
  TeksCategoryList,
  TeksCategoryMeta,
  ObjectivesItem,
  Unit,
  NonUndefined,
} from "@examspark/types";

import { YagDataStructure } from "../types";
import { getObjectiveCategory, getObjectiveCurriculumUsage } from "../utils";

import ObjectiveIndicator from "./ObjectiveIndicator";
import PrintObjectiveBadge from "./PrintObjectiveBadge";

// import YagUnitStandards from "./YagUnitStandards";

/* START move these to common-ui/server */
const Grid = styled.div<{ size?: string; gap?: string }>`
  display: grid;
  gap: ${({ gap = "1rem" }) => gap};
  grid-template-columns: repeat(
    auto-fill,
    minmax(${({ size = "20rem" }) => size}, 1fr)
  );
`;

const VSpace = styled.div<{
  size?: string;
}>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.size || "1rem"};
`;

// const Toolbar = styled.div``;
/* END move these to common-ui/server */

const UnitItem = styled.div`
  break-inside: avoid;
`;

const UnitTitle = styled.div`
  padding: 0.7rem;
  /* background-color: ${colorFromTheme("surface2")}; */
  background-color: hsl(0, 0%, 96%, 1);
  border-top: solid 1px #333;
  border-bottom: solid 1px #333;
  font-size: 1.1rem;
  display: flex;
`;

const UnitBody = styled.div`
  /* padding: 0.5rem 1rem; */
  color: #888;
`;

const UnitName = styled.div`
  color: ${colorFromTheme("secondary", "textColor")};
`;

const UnitDateRange = styled.div`
  color: #333;
  align-self: flex-end;

  span {
    color: #777;
  }
`;

// const StyledStandardsPlaceholder = styled.div`
//   margin: 1rem;
//   text-align: center;
//   font-size: 1.1rem;
//   color: #888;
// `;

const Section = styled.div`
  /* background-color: ${colorFromTheme("surface0")}; */
  border: solid 1px #333;
  /* border-top: solid 3px crimson; */
  margin: 0 0.05in;
  break-inside: avoid;
`;

const SectionTitle = styled.div`
  /* background-color: ${colorFromTheme("surface0")}; */
  background-color: #333;
  color: white;
  font-size: 1.2em;
  /* color: #333; */
  padding: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;

const SectionTitleLength = styled.div`
  font-weight: 300;
`;

const StyledStandards = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid black;
  padding: 0.5rem;
  gap: 1rem;

  &:first-child {
    border-top: none;
  }
`;

const StyledStandardsLabel = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 1.1rem;
  align-items: center;
  padding: 0 0.5rem;
`;

const StyledStandardsList = styled.div``;

type YagPeriodProps = {
  course: Course;
  listStyle?: "grid" | "list";
  showStandardsInGroups: boolean;
  data: YagDataStructure["gradingPeriods"][number];
  objectives: NonUndefined<
    UnwrapGraphQLResult<typeof gql.operations.course.getCourseObjectives>
  >;
};

type TeksCategoryGroup = TeksCategory | "all" | "none";

const YagUnitStandards = ({
  objectives,
  label,
  showIndicator,
}: {
  objectives: ObjectivesItem[];
  unit: Unit | null | undefined;
  label: ReactNode;
  showIndicator: boolean;
}) => {
  if (!objectives.length) return null;

  let gridSize = "7rem";
  if (showIndicator) {
    gridSize = "10rem";
  }

  return (
    <StyledStandards>
      {!!label && (
        <StyledStandardsLabel>
          <div>{label}</div>
        </StyledStandardsLabel>
      )}
      <StyledStandardsList>
        <Grid size={gridSize} gap="0">
          {objectives.map((s, index) => (
            <PrintObjectiveBadge
              key={s.id}
              objectivesItem={s}
              leadingContent={
                showIndicator && (
                  <ObjectiveIndicator
                    value={getObjectiveCategory(s.objective)}
                  />
                )
              }
            />
          ))}
        </Grid>
      </StyledStandardsList>
    </StyledStandards>
  );
};

const StyledYagUnitStandards = styled(YagUnitStandards)`
  break-inside: avoid;
`;

const PrintYagPeriod = ({
  data,
  showStandardsInGroups,
  objectives,
}: YagPeriodProps) => {
  const filterObjectives = (items: ObjectivesItem[] | undefined | null) =>
    sortObjectivesItems(
      items?.filter((item) => {
        const usage = getObjectiveCurriculumUsage(item);
        if (usage !== "DIRECTLY_TAUGHT") return false;
        return true;
      }) ?? [],
    );

  const objectivesByUnit = keyBy(
    objectives.units.map((u) => ({
      id: u.unit?.id ?? "",
      objectivesByGroup: groupBy(
        filterObjectives(u.unit?.objectives ?? []),
        (o) =>
          showStandardsInGroups
            ? (getObjectiveCategory(o.objective) ?? "none")
            : "all",
      ) as Record<TeksCategoryGroup, ObjectivesItem[] | undefined>,
    })),
    (ou) => ou.id,
  );

  const groups: TeksCategoryGroup[] = showStandardsInGroups
    ? [...TeksCategoryList, "none"]
    : ["all"];

  return (
    <Section>
      <SectionTitle>
        <div style={{ flex: 1 }}>{data?.name}</div>
        {data.durationInDays && (
          <SectionTitleLength>
            {data.durationInDays} {pluralize("day", data.durationInDays)}
          </SectionTitleLength>
        )}
      </SectionTitle>
      {data.units?.map((yagUnit) => {
        const unit = yagUnit.unit;

        const objectives = objectivesByUnit[unit.id];

        return (
          <UnitItem key={yagUnit.unit.id}>
            <UnitTitle>
              <UnitName>
                {unit.name} {yagUnit.isContinued ? "(Continued)" : ""}
              </UnitName>{" "}
              <div style={{ flex: "1" }}></div>
              <UnitDateRange>
                {yagUnit.startDate && yagUnit.endDate && (
                  <>
                    {shortLocale.format(fromAWSDate(yagUnit.startDate))} -{" "}
                    {shortLocale.format(fromAWSDate(yagUnit.endDate))}{" "}
                  </>
                )}
                <span>
                  ({yagUnit.daysInUnitInGradingPeriod}{" "}
                  {pluralize("Day", yagUnit.daysInUnitInGradingPeriod)})
                </span>
              </UnitDateRange>
            </UnitTitle>
            <UnitBody>
              <VSpace>
                <div>
                  {groups.map((group) => {
                    const groupObjectives = objectives.objectivesByGroup[group];
                    if (!groupObjectives) return null;
                    return (
                      <StyledYagUnitStandards
                        key={group}
                        objectives={groupObjectives}
                        unit={unit}
                        label={
                          group === "all" ? undefined : group === "none" ? (
                            "Other Student Expectations"
                          ) : (
                            <Toolbar>
                              {TeksCategoryMeta[group].label}
                              <ObjectiveIndicator value={group} />
                            </Toolbar>
                          )
                        }
                        showIndicator={!showStandardsInGroups}
                      />
                    );
                  })}
                </div>
              </VSpace>
            </UnitBody>
          </UnitItem>
        );
      })}
    </Section>
  );
};

export default PrintYagPeriod;
